/**
 * Helpers for reasoning about AWS S3 presigned URL expiry on the client.
 *
 * Presigned URLs carry their own validity window in the query string, so the
 * frontend can tell a stale URL from a live one without the backend having to
 * send an explicit `expiresAt`.
 */

/** Default safety margin: treat a URL as expired 5 minutes before it actually is. */
const DEFAULT_SKEW_MS = 5 * 60 * 1000;

/**
 * Epoch milliseconds at which an AWS presigned URL stops being valid.
 * Returns `null` when the URL is not signed or the params can't be parsed.
 */
export function getSignedUrlExpiryMs(url: string): number | null {
    if (!url) return null;

    try {
        const params = new URL(url, window.location.origin).searchParams;

        // Signature V4: X-Amz-Date=20260727T101530Z & X-Amz-Expires=<seconds>
        const amzDate = params.get('X-Amz-Date');
        const amzExpires = params.get('X-Amz-Expires');
        if (amzDate && amzExpires) {
            const parts = amzDate.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
            const expiresIn = Number(amzExpires);
            if (!parts || Number.isNaN(expiresIn)) return null;
            const signedAt = Date.UTC(+parts[1], +parts[2] - 1, +parts[3], +parts[4], +parts[5], +parts[6]);
            return signedAt + expiresIn * 1000;
        }

        // Signature V2 fallback: Expires=<epoch seconds>
        const v2Expires = params.get('Expires');
        if (v2Expires && !Number.isNaN(Number(v2Expires))) {
            return Number(v2Expires) * 1000;
        }

        return null;
    } catch {
        return null;
    }
}

/**
 * True when the URL is signed AND is already expired (or will be within `skewMs`).
 * Unsigned or unparseable URLs return `false` so they are never needlessly refreshed.
 */
export function isSignedUrlExpired(url: string, skewMs: number = DEFAULT_SKEW_MS): boolean {
    const expiry = getSignedUrlExpiryMs(url);
    return expiry !== null && expiry - skewMs <= Date.now();
}
